import type { AppTopic } from '@/backend/websocket'
import type { EventNamesForTopic, PayloadForEvent, WebSocketTopic } from '@/data/websocketTopics'
import { onScopeDispose, ref, watch, type Ref } from 'vue'
import { useWebSocket, type CleanupFn } from './useWebSocket'

/**
 * Reactive WebSocket multiple topic subscription with automatic cleanup.
 *
 * Subscribes to an array of topics and applies the same handlers to all topics.
 * Automatically manages subscriptions as the topic array changes.
 * Event listeners are automatically cleaned up when the caller goes out of scope.
 *
 * Usage:
 * ```ts
 * const topics = ref(['case:workspace1:case1', 'case:workspace1:case2'])
 * const { subscribe, isConnected } = useWebSocketTopicArray(topics)
 *
 * subscribe('case:updated', (data) => {
 *   console.log('Received update for case:', data.case.id)
 * })
 * ```
 */
export const useWebSocketTopicArray = <T extends WebSocketTopic>(topics: Ref<T[]>) => {
  const ws = useWebSocket()

  /** Maps each topic to its cleanup functions. */
  const cleanupFunctions = ref<Map<string, CleanupFn[]>>(new Map())
  const subscribedTopics = ref<Set<string>>(new Set())

  /** Events and handlers to apply to topics when they connect. */
  const queuedHandlers = ref<
    Array<{ event: string; handler: (payload: unknown, topic: T) => void }>
  >([])

  /**
   * Apply all queued subscriptions to a specific topic
   */
  const applyQueuedHandlersToTopic = (topic: T) => {
    const topicCleanups: CleanupFn[] = []

    queuedHandlers.value.forEach(({ event, handler }) => {
      const wrappedHandler = (payload: unknown) => handler(payload, topic)
      const cleanup = ws.addTopicEventListener(topic as AppTopic, event, wrappedHandler)
      topicCleanups.push(cleanup)
    })

    cleanupFunctions.value.set(topic, topicCleanups)
  }

  /**
   * Subscribe to an event on all current and future topics.
   * Returns a cleanup function, but cleanup is also automatic on scope dispose.
   */
  const subscribe = async <E extends EventNamesForTopic<T>>(
    event: E,
    handler: (payload: PayloadForEvent<T, E>, topic: T) => void,
  ): Promise<() => void> => {
    // We need to cast the event and handler to strings to avoid very complex typing.
    // Consumer gets strict types when using this.
    const untypedEvent = event as string
    const untypedHandler = handler as (payload: unknown, topic: T) => void

    // Add to queued handlers so it gets applied to current and future topics
    const queuedItem = { event: untypedEvent, handler: untypedHandler }
    queuedHandlers.value.push(queuedItem)

    // Apply to all currently subscribed topics
    subscribedTopics.value.forEach((topicString) => {
      // Create a wrapper that passes the topic to the original handler
      const wrappedHandler = (payload: unknown) => untypedHandler(payload, topicString as T)
      const cleanup = ws.addTopicEventListener(
        topicString as AppTopic,
        untypedEvent,
        wrappedHandler,
      )
      const existingCleanups = cleanupFunctions.value.get(topicString) || []
      cleanupFunctions.value.set(topicString, [...existingCleanups, cleanup])
    })

    return () => {
      queuedHandlers.value = queuedHandlers.value.filter((sub) => sub !== queuedItem)
    }
  }

  /**
   * Unsubscribe from a specific topic
   */
  const unsubscribeFromTopic = async (topic: string) => {
    const topicCleanups = cleanupFunctions.value.get(topic) || []
    topicCleanups.forEach((cleanup) => cleanup())
    cleanupFunctions.value.delete(topic)

    if (subscribedTopics.value.has(topic)) {
      await ws.unsubscribeFromTopic(topic as AppTopic)
      subscribedTopics.value.delete(topic)
    }
  }

  /**
   * Subscribe to a specific topic and apply all queued handlers
   */
  const subscribeToTopic = async (topic: T) => {
    if (subscribedTopics.value.has(topic)) return

    await ws.subscribeToTopic(topic as AppTopic)
    subscribedTopics.value.add(topic)
    applyQueuedHandlersToTopic(topic)
  }

  /**
   * Manually unsubscribe from all topics (usually not needed due to auto-cleanup)
   */
  const unsubscribeAll = async () => {
    const allTopics = Array.from(subscribedTopics.value)
    await Promise.all(allTopics.map((topic) => unsubscribeFromTopic(topic)))
  }

  watch(
    topics,
    async (newTopics, oldTopics = []) => {
      const newTopicSet = new Set(newTopics)
      const oldTopicSet = new Set(oldTopics)

      const topicsToRemove = oldTopics.filter((topic) => !newTopicSet.has(topic))
      const topicsToAdd = newTopics.filter((topic) => !oldTopicSet.has(topic))

      await Promise.all([
        ...topicsToRemove.map((topic) => unsubscribeFromTopic(topic)),
        ...topicsToAdd.map((topic) => subscribeToTopic(topic)),
      ])
    },
    { immediate: true },
  )

  onScopeDispose(() => {
    unsubscribeAll()
    queuedHandlers.value = []
  })

  return {
    subscribe,
    subscribedTopics,
  }
}
